import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { GlassButton } from 'dashboard/components/common';
import InputField from 'dashboard/components/fields/InputField';
import { useGlass } from 'styles/glass';
import { friendlyError } from 'dashboard/pages/brand/settings/components/informationDetails/constants';

/**
 * Create a brand. Name and domain, nothing else.
 *
 * Deliberately NOT the Settings form (`InformationDetails`). Creating and
 * editing a brand stopped being the same job: everything optional — logo,
 * contact details, bank/payout info — is configured afterwards from Settings,
 * guided by the onboarding checklist. Bending one component to serve both
 * with a "minimal" flag meant hiding two of three tabs and most of the fields,
 * which is how the create dialog ended up asking for a SWIFT code before the
 * brand existed.
 *
 * With one step there is nothing to tab between, so there is no tab bar here —
 * that lives on Settings, where multi-section editing actually happens.
 */
export default function CreateBrandForm({ onCreated, onCancel }) {
  const GLASS = useGlass();
  const [formData, setFormData] = useState({ name: '', domain: '' });
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errors = useMemo(() => {
    const next = {};
    if (!formData.name?.trim()) next.name = 'Brand name is required';
    if (!formData.domain?.trim()) next.domain = 'Brand domain is required';
    return next;
  }, [formData]);

  const isValid = Object.keys(errors).length === 0;

  const disabledReason = useMemo(() => {
    if (isSubmitting) return 'Already creating — hang on a moment.';
    const missing = Object.keys(errors);
    if (missing.length === 0) return '';
    const names = missing.map((k) => errors[k].replace(/ is required$/i, ''));
    return `Still needed: ${names.join(' and ')}.`;
  }, [errors, isSubmitting]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleBlur = (e) => setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  const showError = (field) => (submitted || touched[field]) && Boolean(errors[field]);

  const fieldProps = (name) => ({
    mb: '0px',
    id: name,
    name,
    value: formData[name] || '',
    onChange: handleChange,
    onBlur: handleBlur,
    isInvalid: showError(name),
    errorText: errors[name],
    isRequired: true,
  });

  const handleSubmit = (event) => {
    if (event?.preventDefault) event.preventDefault();
    setSubmitted(true);
    if (!isValid) return;

    // The endpoint takes multipart (it also accepts a logo on update), so the
    // payload goes in a `data` part even though there is no file here.
    const body = new FormData();
    body.append('data', JSON.stringify({ ...formData }));

    setIsSubmitting(true);
    API.updateBrandData(body)
      .then((response) => {
        // Own the wording rather than echoing the server's — the endpoint is
        // shared with the update path and speaks in `company`, which is the
        // collection name, not what the user just made.
        toast.success('Brand created successfully');
        // The id matters: it is what selects the new brand and lands the user
        // on its overview rather than leaving them on the previous one.
        onCreated?.(response?.brand_id || response?.company?._id);
      })
      .catch((err) => toast.error(friendlyError(err?.message || err)))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Stack direction='column' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: '16px', md: '24px' }, py: '20px' }}>
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: '20px' }}>
          Just the essentials — you can add your logo, contact details and payout
          info from Settings once the brand exists.
        </Typography>
        <Stack direction='column' spacing='20px' sx={{ maxWidth: 560 }}>
          <InputField {...fieldProps('name')} placeholder='Brand Name' label='Brand Name' />
          <InputField {...fieldProps('domain')} placeholder='https://example.com' label='Brand Domain' />
        </Stack>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: { xs: '16px', md: '24px' },
          py: '16px',
          borderTop: GLASS.divider,
        }}
      >
        <Stack direction='row' justifyContent='flex-end' alignItems='center' gap='12px'>
          {onCancel && (
            <GlassButton onClick={onCancel} disabled={isSubmitting}>Cancel</GlassButton>
          )}
          <GlassButton
            type='submit'
            variant='contained'
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            disabledReason={disabledReason}
            sx={{ borderRadius: '16px', width: { xs: '128px', md: '148px' }, height: '46px' }}
          >
            {isSubmitting ? 'Creating…' : 'Create Brand'}
          </GlassButton>
        </Stack>
      </Box>
    </Stack>
  );
}
